import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUser } from '../context/UserContext';
import Select from 'react-select';
import { CSVLink } from 'react-csv';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import 'react-datepicker/dist/react-datepicker.css';

const CoachAttendance = () => {
  const { selectedCenter } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [academyOptions, setAcademyOptions] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [coachOptions, setCoachOptions] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const token = localStorage.getItem('auth-token');
  const [selectedSport, setSelectedSport] = useState(null);
  const [sportOptions, setSportOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [academiesLoaded, setAcademiesLoaded] = useState(false);

  const headers = useMemo(() => ([
    { label: "Center", key: "centerName" },
    { label: "Coach Name", key: "coachName" },
    { label: "Sport", key: "sport" },
    { label: "Coach Phone", key: "coachPhone" },
    { label: "Check-in Date", key: "checkinDate" },
    { label: "Check-in Time", key: "checkinTime" },
    { label: "Check-out Time", key: "checkoutTime" },
    { label: "Remark", key: "reason" },
    { label: "Status", key: "status" },
  ]), []);

  const statusOptions = [
    { value: "Success", label: "Success" },
    { value: "Failure", label: "Failure" },
  ];

  const fetchAcademyAndCoach = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(process.env.REACT_APP_API_COACH_LIST, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
      });
      const data = await res.json();

      if (!data?.success || !data?.data?.data?.length) {
        setError('Received invalid or empty data from the Academy API.');
        setAcademiesLoaded(false);
        return;
      }

      const academies = data.data.data.map(a => ({
        value: String(a.academyId),
        label: a.academyName,
      }));

      const coachesList = data.data.data.flatMap((academy) =>
        (academy.coach || []).map((coach) => ({
          value: coach.coachUserId,
          label: (coach.coachName || '').trim(),
          academy: String(academy.academyId),
          contact: coach.contactNo,
          coachType: coach.coachType,
        }))
      );

      setAcademyOptions(academies);
      setCoachOptions(coachesList);

      const match = academies.find(a => a.value === String(selectedCenter));
      setSelectedAcademy(match || academies[0] || null);

      setAcademiesLoaded(true);
    } catch (e) {
      console.error('Error fetching academy/coach:', e);
      setError('Failed to fetch academy/coach data.');
      setAcademiesLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceData = async () => {
    if (!academiesLoaded) return;
    setLoading(true);
    setError('');
    try {
      const url = `${process.env.REACT_APP_API_URL_COACH_ATTENDANCE}?startDate=${startDate}&endDate=${endDate}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const result = await res.json();
      const items = result?.data?.items || [];

      const formatTime = (timeStr) => {
        if (!timeStr || timeStr === '-') return '-';
        const [hour, minute, second] = timeStr.split(':');
        const d = new Date();
        d.setHours(Number(hour || 0), Number(minute || 0), Number(second || 0));
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
      };

      const formatted = items.map(item => ({
        academyid: item.academyId ?? 'Unknown Academy',
        academyName: item.academyName || 'Unknown Academy',
        coachName: item.coachName || 'N/A',
        coachPhone: item.mobileNumber || 'N/A',
        checkinDate: item.checkInDate ? new Date(item.checkInDate).toLocaleDateString() : 'N/A',
        checkinTime: formatTime(item.checkInTime),
        checkoutTime: formatTime(item.checkOutTime),
        type: item.checkOutTime ? 'checkout' : 'checkin',
        sport: item.sportName || 'N/A',
        centerName: item.academyName || 'Unknown Academy',
        reason: item.reason || '-',
        status: item.status === true ? 'Success' : 'Failure',
      }));

      const uniqueSports = [...new Set(formatted.map(c => c.sport))]
        .filter(s => s && s !== 'N/A')
        .map(s => ({ value: s, label: s }));
      setSportOptions(uniqueSports);

      setAttendanceData(formatted);
    } catch (e) {
      console.error('Error fetching attendance data:', e);
      setError('Failed to fetch attendance data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcademyAndCoach();
  }, []);

  useEffect(() => {
    if (!academiesLoaded) return;
    fetchAttendanceData();
  }, [academiesLoaded, startDate, endDate, selectedAcademy]);

  useEffect(() => {
    if (!academyOptions.length || !selectedCenter) return;
    const match = academyOptions.find(a => a.value === String(selectedCenter));
    if (match && (!selectedAcademy || selectedAcademy.value !== match.value)) {
      setSelectedAcademy(match);
      setSelectedCoach(null);
    }
  }, [academyOptions, selectedCenter]);

  const handleItemsPerPageChange = (e) => { setrowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const filteredData = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    return attendanceData.filter((item) => {
      const searchMatch =
        !term ||
        (item.coachName && item.coachName.toLowerCase().includes(term)) ||
        (item.coachPhone || '').includes(searchTerm) ||
        (item.centerName && item.centerName.toLowerCase().includes(term));

      const sportMatch = !selectedSport || item.sport === selectedSport.value;
      const coachMatch = !selectedCoach || item.coachName === selectedCoach.label;
      const centerMatch = !selectedAcademy || String(item.academyid) === String(selectedAcademy.value);
      const statusMatch = !selectedStatus || item.status === selectedStatus.value;

      return searchMatch && sportMatch && coachMatch && centerMatch && statusMatch;
    });
  }, [attendanceData, searchTerm, selectedSport, selectedCoach, selectedAcademy, selectedStatus, searchTerm]);

  const [sortConfig, setSortConfig] = useState({ key: 'checkinDate', direction: 'ascending' });
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    return [...filteredData].sort((a, b) => {
      const x = a[key] ?? '';
      const y = b[key] ?? '';
      if (x < y) return direction === 'ascending' ? -1 : 1;
      if (x > y) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const renderPagination = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages.map((p, idx) =>
      typeof p === 'number' ? (
        <button key={idx} className={`page-number ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>
          {p}
        </button>
      ) : (
        <span key={idx} className="pagination-dots">{p}</span>
      )
    );
  };

  const totalEntries = filteredData.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">
      <div className="sales-filters-login">
        {/* Center */}
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedAcademy ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={academyOptions}
            value={selectedAcademy}
            onChange={(opt) => { setSelectedAcademy(opt || null); setSelectedCoach(null); setCurrentPage(1); }}
            placeholder="Select Center"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              placeholder: (p) => ({ ...p, color: 'black' }),
              menuPortal: (b) => ({ ...b, zIndex: 9999 }),
            }}
            isClearable
          />
          {selectedAcademy && <label htmlFor="selectedAcademy">Center</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedCoach ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={coachOptions.filter(c => !selectedAcademy || String(c.academy) === String(selectedAcademy?.value))}
            value={selectedCoach}
            onChange={(opt) => { setSelectedCoach(opt); setCurrentPage(1); }}
            placeholder="Select Coach"
            isClearable
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              placeholder: (p) => ({ ...p, color: 'black' }),
              menuPortal: (b) => ({ ...b, zIndex: 9999 }),
            }}
          />
          {selectedCoach && <label htmlFor="selectedCoach">Coach</label>}
        </div>

        {/* Sport */}
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedSport ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={sportOptions}
            value={selectedSport}
            onChange={(opt) => { setSelectedSport(opt); setCurrentPage(1); }}
            placeholder="Select Sport"
            isClearable
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              placeholder: (p) => ({ ...p, color: 'black' }),
              menuPortal: (b) => ({ ...b, zIndex: 9999 }),
            }}
          />
          {selectedSport && <label htmlFor="selectedSport">Sport</label>}
        </div>

        {/* Status */}
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedStatus ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={statusOptions}
            value={selectedStatus}
            onChange={(opt) => { setSelectedStatus(opt); setCurrentPage(1); }}
            placeholder="Select Status"
            isClearable
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              placeholder: (p) => ({ ...p, color: 'black' }),
              menuPortal: (b) => ({ ...b, zIndex: 9999 }),
            }}
          />
          {selectedStatus && <label htmlFor="selectedStatus">Status</label>}
        </div>

        {/* Dates */}
        <div className="floating-select">
          <input
            type="date"
            id="startDate"
            className={`sales-filter-academy ${startDate ? 'has-value' : ''}`}
            value={startDate || ''}
            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          {startDate && <label htmlFor="startDate">Start Date</label>}
        </div>
        <div className="floating-select">
          <input
            type="date"
            id="endDate"
            className={`sales-filter-academy ${endDate ? 'has-value' : ''}`}
            value={endDate || ''}
            min={startDate || ''}
            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          {endDate && <label htmlFor="endDate">End Date</label>}
        </div>
      </div>

      {/* Search + Export */}
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>

        <CSVLink
          data={filteredData}
          headers={headers}
          filename="Coach_Attendance.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button">
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,1,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      {/* Controls */}
      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchAttendanceData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track"><div className="progress-bar"></div></div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredData.length === 0 && (
        <div className="error-message">No attendance data available</div>
      )}

      {/* Table */}
      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.direction === 'descending' && sortConfig.key === key ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? currentRows.map((item, idx) => (
              <tr key={idx}>
                <td>{item.centerName}</td>
                <td>{item.coachName}</td>
                <td>{item.sport}</td>
                <td>{item.coachPhone}</td>
                <td>{item.checkinDate}</td>
                <td>{item.checkinTime}</td>
                <td>{item.checkoutTime}</td>
                <td>{item.reason}</td>
                <td>{item.status}</td>
              </tr>
            )) : (
              <tr><td colSpan={headers.length} style={{ textAlign: 'center' }}>No data available</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default CoachAttendance;
